import { useCallback, useEffect, useMemo, useState } from "react";
import { CheckCircle2, Circle, Plus, Loader2, AlertTriangle, Sparkles, Clock, Send, Trash2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useT } from "@/i18n/I18nContext";

const PRIORITY_STYLE = {
  urgent: "bg-[#FDE8E6] text-[#B91C1C] border-[#F5B6B0]",
  high:   "bg-[#fff5e6] text-[#B45309] border-[#fcd9a3]",
  medium: "bg-[#FDFBF7] text-[#5C6B68] border-[#E0E4E2]",
  low:    "bg-[#E6F6ED] text-[#166534] border-[#bce5cc]",
};

function dueChip(t, dueAt) {
  if (!dueAt) return null;
  const now = new Date();
  const due = new Date(dueAt);
  const ms = due - now;
  const hours = Math.round(ms / 3_600_000);
  const days = Math.round(ms / 86_400_000);
  const overdue = ms < 0;
  let label;
  if (overdue) label = t("tasks.overdue");
  else if (hours < 24) label = t("tasks.due_in_hours", { n: Math.max(1, hours) });
  else label = t("tasks.due_in_days", { n: days });
  const tone = overdue
    ? "bg-[#FDE8E6] text-[#B91C1C] border-[#F5B6B0]"
    : hours <= 24
      ? "bg-[#fff5e6] text-[#B45309] border-[#fcd9a3]"
      : "bg-[#FDFBF7] text-[#5C6B68] border-[#E0E4E2]";
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${tone}`}>
      <Clock className="h-2.5 w-2.5" />
      {label}
    </span>
  );
}

/**
 * Renders a list of workflow + manual tasks for a single case, with
 * optimistic toggle, manual-add form, and overdue/priority badges.
 *
 * Props:
 *   caseId, scope: 'attorney' | 'admin'
 *   onChange(): optional callback fired when a task is added/toggled/deleted
 *   compact: hide the "Add" form (used in dashboard widgets)
 */
export default function TasksList({ caseId, scope = "attorney", onChange, compact = false }) {
  const t = useT();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newPriority, setNewPriority] = useState("medium");
  const [newDueHours, setNewDueHours] = useState(24);

  const basePath = scope === "admin" ? "/admin/tasks" : "/attorney/tasks";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(basePath, { params: { case_id: caseId } });
      setTasks(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [basePath, caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const grouped = useMemo(() => {
    const pending = tasks.filter((t) => t.status === "pending");
    const done = tasks.filter((t) => t.status === "done");
    return { pending, done };
  }, [tasks]);

  const toggleTask = async (task) => {
    const nextStatus = task.status === "done" ? "pending" : "done";
    // optimistic
    setTasks((prev) =>
      prev.map((t) => (t.id === task.id ? { ...t, status: nextStatus } : t)),
    );
    try {
      const { data } = await api.patch(`${basePath}/${task.id}`, { status: nextStatus });
      setTasks((prev) => prev.map((t) => (t.id === task.id ? data : t)));
      onChange?.();
    } catch (e) {
      // revert on failure
      setTasks((prev) =>
        prev.map((t) => (t.id === task.id ? { ...t, status: task.status } : t)),
      );
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const addTask = async (e) => {
    e.preventDefault();
    if (!newTitle.trim()) return;
    setAdding(true);
    try {
      const dueAt = new Date(Date.now() + newDueHours * 3_600_000).toISOString();
      const { data } = await api.post(basePath, {
        case_id: caseId,
        title: newTitle.trim(),
        priority: newPriority,
        due_at: dueAt,
      });
      setTasks((prev) => [data, ...prev]);
      setNewTitle("");
      setNewPriority("medium");
      setNewDueHours(24);
      onChange?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setAdding(false);
    }
  };

  const removeTask = async (task) => {
    if (scope !== "admin") return; // attorneys can only complete, not delete
    if (!confirm(t("tasks.confirm_delete"))) return;
    try {
      await api.delete(`${basePath}/${task.id}`);
      setTasks((prev) => prev.filter((t) => t.id !== task.id));
      onChange?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 p-6 text-sm text-[#5C6B68]" data-testid="tasks-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> {t("tasks.loading")}
      </div>
    );
  }

  return (
    <div data-testid="tasks-list">
      {!compact && (
        <form onSubmit={addTask} className="flex flex-wrap gap-2" data-testid="tasks-add-form">
          <input
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
            placeholder={t("tasks.add_placeholder")}
            className="input-field flex-1 min-w-[200px]"
            data-testid="tasks-add-title"
          />
          <select
            value={newPriority}
            onChange={(e) => setNewPriority(e.target.value)}
            className="input-field !w-auto"
            data-testid="tasks-add-priority"
          >
            <option value="low">{t("tasks.prio.low")}</option>
            <option value="medium">{t("tasks.prio.medium")}</option>
            <option value="high">{t("tasks.prio.high")}</option>
            <option value="urgent">{t("tasks.prio.urgent")}</option>
          </select>
          <select
            value={newDueHours}
            onChange={(e) => setNewDueHours(parseInt(e.target.value, 10))}
            className="input-field !w-auto"
            data-testid="tasks-add-due"
          >
            <option value="1">{t("tasks.due_opt.1h")}</option>
            <option value="24">{t("tasks.due_opt.1d")}</option>
            <option value="72">{t("tasks.due_opt.3d")}</option>
            <option value="168">{t("tasks.due_opt.1w")}</option>
            <option value="336">{t("tasks.due_opt.2w")}</option>
          </select>
          <button type="submit" disabled={adding || !newTitle.trim()} className="btn-primary disabled:opacity-50" data-testid="tasks-add-submit">
            {adding ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </button>
        </form>
      )}

      {grouped.pending.length === 0 && grouped.done.length === 0 && (
        <div className="mt-4 card-soft p-6 text-center text-sm text-[#5C6B68]" data-testid="tasks-empty">
          <Sparkles className="mx-auto h-5 w-5 text-[#E85D04]" />
          <div className="mt-2 font-display font-bold text-[#11322A]">{t("tasks.empty_title")}</div>
          <p className="mt-1 text-xs">{t("tasks.empty_body")}</p>
        </div>
      )}

      {grouped.pending.length > 0 && (
        <div className="mt-4">
          <div className="label-cap">{t("tasks.heading.pending")} ({grouped.pending.length})</div>
          <ul className="mt-2 divide-y divide-[#E0E4E2] rounded-xl border border-[#E0E4E2] bg-white">
            {grouped.pending.map((task) => (
              <li key={task.id} className="flex items-start gap-3 p-3" data-testid={`task-${task.id}`}>
                <button
                  type="button"
                  onClick={() => toggleTask(task)}
                  className="mt-0.5 text-[#5C6B68] hover:text-[#11322A]"
                  data-testid={`task-toggle-${task.id}`}
                  aria-label="Mark complete"
                >
                  <Circle className="h-5 w-5" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="font-display text-sm font-bold text-[#0B1317]">{task.title}</div>
                  {task.description && <p className="mt-0.5 text-xs text-[#5C6B68]">{task.description}</p>}
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${PRIORITY_STYLE[task.priority] || PRIORITY_STYLE.medium}`}>
                      {t(`tasks.prio.${task.priority}`)}
                    </span>
                    {dueChip(t, task.due_at)}
                    {task.source === "workflow" && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-[#E0E4E2] bg-[#FDFBF7] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#5C6B68]">
                        <Sparkles className="h-2.5 w-2.5" /> {t("tasks.workflow_pill")}
                      </span>
                    )}
                  </div>
                </div>
                {scope === "admin" && (
                  <button
                    type="button"
                    onClick={() => removeTask(task)}
                    className="text-[#B91C1C] hover:text-[#7f1d1d]"
                    data-testid={`task-remove-${task.id}`}
                    aria-label="Delete task"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {grouped.done.length > 0 && (
        <div className="mt-4">
          <div className="label-cap">{t("tasks.heading.done")} ({grouped.done.length})</div>
          <ul className="mt-2 space-y-2">
            {grouped.done.map((task) => (
              <li key={task.id} className="flex items-start gap-3 rounded-lg bg-[#FDFBF7] px-3 py-2 text-xs text-[#5C6B68]">
                <button
                  type="button"
                  onClick={() => toggleTask(task)}
                  className="mt-0.5 text-[#166534]"
                  aria-label="Reopen"
                  data-testid={`task-toggle-${task.id}`}
                >
                  <CheckCircle2 className="h-4 w-4" />
                </button>
                <div className="min-w-0 flex-1 line-through opacity-70">{task.title}</div>
                {task.completed_by_name && (
                  <div className="text-[10px] text-[#5C6B68]">{task.completed_by_name}</div>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
